/*
 * Build checks tab: pure model, no React. The icon and tone per status, the SPEC §16 check codes, and what each row
 * shows (message, details, code).
 */
import type { BuildCheck } from '@/api/schemas';

const ICON: Record<string, string> = { passed: 'check_circle', failed: 'cancel', error: 'error', warning: 'warning', skipped: 'remove_circle' };
const COLOR: Record<string, string> = { passed: 'text-succeeded', failed: 'text-failed', error: 'text-failed', warning: 'text-awaiting', skipped: 'text-subtle' };

export const checkIcon = (status: string): string => ICON[status] ?? 'help';
export const checkColor = (status: string): string => COLOR[status] ?? 'text-subtle';

/** SPEC §16 check codes (`error.code` of a build failed by a blocking check); unknown keys derive HGR-CHECK-<KEY>. */
const CHECK_CODE: Record<string, string> = { env_assertion: 'HGR-CHECK-ENV', signing_fingerprint: 'HGR-CHECK-SIGNING', size_budget: 'HGR-CHECK-SIZE', bundle_id: 'HGR-CHECK-BUNDLE-ID', version_monotonic: 'HGR-CHECK-VERSION', target_sdk: 'HGR-CHECK-TARGET-SDK' };
export const checkCode = (key: string): string => CHECK_CODE[key] ?? `HGR-CHECK-${key.replace(/[^a-z0-9]+/gi, '-').toUpperCase()}`;

/** A non-empty string, else `undefined`. */
const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** A blocking check that failed or errored stops the build: its row is drawn in the failed tone. */
export const isFailedBlocking = (c: Pick<BuildCheck, 'blocking' | 'status'>): boolean => Boolean(c.blocking) && (c.status === 'failed' || c.status === 'error');

/** The line under the check: the details' own message, else the summary. */
export const checkMessage = (c: Pick<BuildCheck, 'details' | 'summary'>): string | undefined => str(c.details?.message) ?? c.summary ?? undefined;

/** The check's code shows unless it passed or was skipped. */
export const showsCheckCode = (status: string): boolean => status !== 'passed' && status !== 'skipped';

/** A detail value as text: scalars as they are, anything else as JSON. */
export const detailValue = (v: unknown): string => (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : JSON.stringify(v));

/** The details apart from `message` (shown on its own line), as key/value text pairs. */
export function detailRows(details: BuildCheck['details']): [string, string][] {
  return Object.entries(details ?? {})
    .filter(([k]) => k !== 'message')
    .map(([k, v]) => [k, detailValue(v)]);
}
